import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { HASURA_CLAIMS_NAMESPACE, USER_ID_CLAIM, UserClaims } from "common/models";
import { useLazyQuery } from "@apollo/client";
import { GET_USER_PROFILE, GetUserProfileData, GetUserProfileVars } from "common/graphql";
import { redirectToApplicationByRoleType } from "@app.automotus.io/components/auth";
import AuthLayout from "@app.automotus.io/components/Layout/AuthLayout";

const SSO_ERROR_MESSAGES: Record<string, React.ReactNode> = {
  unauthorized_domain: "Your email domain is not configured for SSO.",
  no_account: (
    <>
      No CurbSuite account found for this email.{" "}
      <a href={process.env.REACT_APP_CURBSUITE_ZENDESK_SUPPORT_TICKET_URL} target="_blank" rel="noreferrer">
        Contact CurbSuite support
      </a>
      .
    </>
  ),
  wrong_account_type: "SSO is only available for CurbSuite accounts.",
  customer_mismatch: "Your account does not match this SSO provider.",
  sso_disabled: "SSO has been disabled for your organization.",
};

/**
 * This route authorizes a user who logged in with passwordless via the universal login flow, ensuring that the Auth0
 * context contains the user's authentication information.
 *
 * When Auth0 denies access (e.g. SSO errors), it redirects here with error and error_description query params.
 * Those are intercepted and displayed before the normal auth flow runs.
 */
export const Authorize: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, user: userClaims } = useAuth0<UserClaims>();
  const [getUserProfile] = useLazyQuery<GetUserProfileData, GetUserProfileVars>(GET_USER_PROFILE);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    if (error) {
      return;
    }

    (async () => {
      if (isAuthenticated) {
        const hasuraClaims = userClaims?.[HASURA_CLAIMS_NAMESPACE];
        const { data: userProfileData } = await getUserProfile({
          variables: {
            userId: hasuraClaims?.[USER_ID_CLAIM] || "",
            // Any descendents of a parent account context should have the same
            // role type, so we do not need to accurately determine the selected
            // account context here to determine the role type of the user.
            activeAccountId: "",
            hasActiveAccountId: false,
          },
        });

        redirectToApplicationByRoleType(userProfileData?.userProfile);

        navigate("/");
        return;
      }

      const claimInvoiceId = sessionStorage.getItem("claimInvoiceId");

      await loginWithRedirect({
        redirectUri: claimInvoiceId ? `${window.location.origin}/paybill/claims` : `${window.location.origin}`,
      });
    })();
  }, [error, isAuthenticated, loginWithRedirect, navigate, userClaims, getUserProfile]);

  if (error) {
    const message = (errorDescription && SSO_ERROR_MESSAGES[errorDescription]) ?? errorDescription ?? error;

    return (
      <AuthLayout>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Sign in failed
          </Typography>
          <Alert severity="error">{message}</Alert>
          <Button variant="contained" fullWidth component={Link} to="/login">
            Back to sign in
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: "100vh", justifyContent: "center", alignItems: "center" }}>
      <CircularProgress />
    </Box>
  );
};

export default Authorize;
